import { z } from "zod";
import type { SitePage } from "./types";

export const GOOGLE_REVIEWS_CONSENT_VERSION = "google-reviews-storage-v1";
export const GOOGLE_REVIEWS_DAILY_LIMIT = 3;
export const GOOGLE_REVIEWS_MONTHLY_LIMIT = 12;
export const googlePlaceIdSchema = z.string().trim().max(255).regex(/^[A-Za-z0-9_-]{10,255}$/, "Enter a Google Place ID, not a Maps link");
/** Links only; no arbitrary embeds, credentials, tracking scripts or remote avatars. */
export function safeGoogleReviewUrl(value: unknown): string {
  if (typeof value !== "string" || value.length > 2048) return "";
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password && !url.port
      && ["google.com", "www.google.com", "maps.google.com", "search.google.com", "g.page", "maps.app.goo.gl"].includes(url.hostname) ? url.href : "";
  } catch { return ""; }
}
const googleUrl = z.string().max(2048).refine(value => !value || Boolean(safeGoogleReviewUrl(value)), "Use a Google review link");
export const googleReviewSchema = z.object({
  id: z.string().regex(/^[a-f0-9]{24}$/), author: z.string().max(120), authorUrl: googleUrl,
  rating: z.number().int().min(1).max(5), text: z.string().max(10000), language: z.string().max(20),
  publishedAt: z.string().datetime().nullable(), reviewUrl: googleUrl
}).strict();
export const googleReviewsSnapshotSchema = z.object({
  placeId: googlePlaceIdSchema, placeName: z.string().max(200), mapsUrl: googleUrl, reviewUrl: googleUrl,
  rating: z.number().min(0).max(5).nullable(), totalReviews: z.number().int().nonnegative(),
  fetchedAt: z.string().datetime(), reviews: z.array(googleReviewSchema).max(5),
  selectedIds: z.array(z.string().regex(/^[a-f0-9]{24}$/)).max(5)
}).strict().refine(data => new Set(data.reviews.map(review => review.id)).size === data.reviews.length && new Set(data.selectedIds).size === data.selectedIds.length && data.selectedIds.every(id => data.reviews.some(review => review.id === id)), "Choose unique reviews from the imported snapshot");
export type GoogleReviewsSnapshot = z.infer<typeof googleReviewsSnapshotSchema>;
export type GoogleReview = z.infer<typeof googleReviewSchema>;
export type GoogleReviewsStatus = {
  configured: boolean; snapshot: GoogleReviewsSnapshot | null;
  usage: { day: string; month: string; daily: number; monthly: number; dailyLimit: number; monthlyLimit: number };
};
export function reviewPage(page: SitePage) {
  return !page.legalNoticeKind && !page.sections.some(section => section.visible && section.type === "testimonials" && section.replaceGoogleWidget) && (!page.slug || page.template === "contact" || page.slug === "contact");
}
